import OperationsOverview from "../components/OperationsOverview";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, ArrowUpCircle, ArrowDownCircle, Gauge, ChevronRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import AddWebsiteDialog from "../components/AddWebsiteDialog";
import MonitorQuota from "../components/MonitorQuota";
import { StatusBadge, StatusDot } from "../components/StatusBadge";
import { staggerContainer, fadeUpItem, ScrollReveal } from "../components/Motion";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  fmtPct,
  fmtMs,
  fmtDate,
  timeAgo,
  siteVariant,
  siteDisplay,
  sslVariant,
  domainVariant,
  dnsVariant,
  perfVariant,
} from "../lib/monitor";

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const toneMap = {
    brand: "text-blue-600 bg-blue-50",
    up: "text-emerald-600 bg-emerald-50",
    down: "text-red-600 bg-red-50",
    neutral: "text-slate-600 bg-slate-100",
  };
  return (
    <Card className="om-premium-card h-full p-4 sm:p-6 xl:p-7" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">{label}</p>
          <p className="mt-3 font-mono text-[clamp(1.7rem,2.1vw,2.5rem)] font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`rounded-lg p-2 ${toneMap[tone]}`}>
          <Icon size={18} strokeWidth={2} />
        </span>
      </div>
    </Card>
  );
}

function MiniIndicator({ variant, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <StatusDot variant={variant} />
      <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [operations, setOperations] = useState(null);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, w, a, o] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/websites"),
        api.get("/alerts"),
        api.get("/operations/overview"),
      ]);
      setError("");
      setUpdatedAt(new Date());
      setOperations(o.data);
      setStats(s.data);
      setSites(w.data);
      setAlerts(a.data.slice(0, 6));
    } catch {
      setError("Could not refresh monitoring data. Any values below are from the last successful update.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard-skeleton">
        <div className="flex items-center justify-between">
          <div className="space-y-2"><Skeleton className="h-8 w-40" /><Skeleton className="h-4 w-56" /></div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 xl:gap-5">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="border-slate-200 p-6"><Skeleton className="h-3 w-24" /><Skeleton className="mt-3 h-8 w-16" /></Card>
          ))}
        </div>
        <Card className="om-premium-card p-6">
          <Skeleton className="mb-4 h-4 w-40" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 py-3.5 last:border-0">
              <Skeleton className="h-4 w-48" /><Skeleton className="h-5 w-16 rounded-full" />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-9" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Overview</h1>
          <p className="mt-1 text-sm text-slate-500">Are your webapp's healthy right now?</p>
        </div>
        <AddWebsiteDialog onAdded={load} />
      </div>

      {error && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600"><p className="flex-1">{error}</p><button className="underline font-semibold" onClick={load}>Retry</button></div>}
      {updatedAt && <p className="text-xs text-slate-500">Updated {updatedAt.toLocaleTimeString()} · refreshes every 30 seconds</p>}
      {!error && <MonitorQuota refreshKey={sites.length} />}
      <OperationsOverview data={operations} />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
      >
        <motion.div variants={fadeUpItem}>
          <StatCard label="Total Webapps" value={stats?.total ?? "—"} icon={Globe} tone="brand" testid="stat-total" />
        </motion.div>
        <motion.div variants={fadeUpItem}>
          <StatCard label="Webapps Up" value={stats?.up ?? "—"} icon={ArrowUpCircle} tone="up" testid="stat-up" />
        </motion.div>
        <motion.div variants={fadeUpItem}>
          <StatCard label="Webapps Down" value={stats?.down ?? "—"} icon={ArrowDownCircle} tone="down" testid="stat-down" />
        </motion.div>
        <motion.div variants={fadeUpItem}>
          <StatCard label="Sample uptime 24h" value={fmtPct(stats?.avg_uptime)} icon={Gauge} tone="neutral" testid="stat-uptime" />
        </motion.div>
      </motion.div>

      <ScrollReveal>
        <h2 className="mb-3 font-heading text-lg font-semibold tracking-tight text-slate-900">Webapp Monitoring</h2>
        <Card className="om-premium-card overflow-hidden">
          {sites.length === 0 ? (
            <div className="px-5 py-10 sm:px-10 sm:py-14" data-testid="dashboard-empty">
              <div className="mx-auto max-w-xl text-center">
                <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                  <Globe size={28} aria-hidden="true" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Get started with MonitorFlux</p>
                <h3 className="mt-3 font-heading text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                  Your first webapp is just a minute away
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-600">
                  Add a public HTTPS address to begin availability, response-time and certificate monitoring. No code changes needed.
                </p>
                <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <AddWebsiteDialog onAdded={load} />
                  <button type="button" onClick={() => navigate("/settings")}
                    className="min-h-10 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    Configure notifications
                  </button>
                </div>
                <ol className="mt-9 grid gap-3 text-left sm:grid-cols-3">
                  {[
                    ["01", "Add your webapp", "Choose its URL and check interval."],
                    ["02", "Track its health", "See uptime, latency and TLS signals."],
                    ["03", "Get notified", "Choose alerts in your settings."]
                  ].map(([number, title, description]) => (
                    <li key={number} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                      <span className="font-mono text-xs font-semibold text-blue-700">{number}</span>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{title}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ) : (
            <>
            <div className="space-y-3 p-3 md:hidden" aria-label="Webapp health overview">
              {sites.map(site => (
                <button key={site.id} type="button" onClick={() => navigate(`/websites/${site.id}`)}
                  className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
                  <div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{site.name}</p><p className="mt-1 truncate text-xs text-slate-500">{site.url}</p></div><StatusBadge variant={siteDisplay(site).variant}>{siteDisplay(site).label}</StatusBadge></div>
                  <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3"><div><p className="text-[11px] text-slate-500">Uptime · 24h</p><p className="mt-1 font-mono text-sm font-semibold text-slate-800">{fmtPct(site.uptime_24h)}</p></div><div><p className="text-[11px] text-slate-500">Response time</p><p className="mt-1 font-mono text-sm font-semibold text-slate-800">{fmtMs(site.last_response_time)}</p></div></div>
                </button>
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-[850px] w-full text-sm" data-testid="dashboard-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    {["Webapp", "Status", "Uptime 24h", "Response", "SSL", "Domain", "DNS", "Perf", ""].map((h, i) => (
                      <th
                        key={h + i}
                        className={`px-4 py-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${
                          i >= 2 && i <= 3 ? "text-right" : i >= 4 ? "text-center" : ""
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sites.map((site) => (
                    <motion.tr
                      key={site.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => navigate(`/websites/${site.id}`)}
                      tabIndex={0}
                      aria-label={`Open ${site.name}`}
                      onKeyDown={e => { if (e.key === "Enter") navigate(`/websites/${site.id}`); }}
                      data-testid={`dashboard-row-${site.id}`}
                      className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-slate-900">{site.name}</div>
                        <div className="font-mono text-xs text-slate-400">{site.url}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusBadge variant={siteDisplay(site).variant} pulse={!site.paused && site.status !== "UNKNOWN"}>{siteDisplay(site).label}</StatusBadge>
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono text-slate-700">{fmtPct(site.uptime_24h)}</td>
                      <td className="px-4 py-3.5 text-right font-mono text-slate-700">{fmtMs(site.last_response_time)}</td>
                      <td className="px-4 py-3.5"><div className="flex justify-center"><StatusDot variant={sslVariant(site.ssl)} /></div></td>
                      <td className="px-4 py-3.5"><div className="flex justify-center"><StatusDot variant={domainVariant(site.domain)} /></div></td>
                      <td className="px-4 py-3.5"><div className="flex justify-center"><StatusDot variant={dnsVariant(site.dns)} /></div></td>
                      <td className="px-4 py-3.5"><div className="flex justify-center"><StatusDot variant={perfVariant(site.performance)} /></div></td>
                      <td className="px-4 py-3.5 text-right"><ChevronRight size={16} className="text-slate-300" /></td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </Card>
      </ScrollReveal>

      <ScrollReveal>
        <h2 className="mb-3 font-heading text-lg font-semibold tracking-tight text-slate-900">Recent Alerts</h2>
        <Card className="om-premium-card p-2">
          {alerts.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">No recent alerts.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {alerts.map((a) => (
                <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-4 sm:flex-nowrap sm:gap-4 sm:px-4" data-testid="dashboard-alert">
                  <div className="flex min-w-0 items-start gap-3">
                    <StatusDot variant={a.severity === "critical" ? "down" : a.severity === "info" ? "up" : "warn"} />
                    <div>
                      <p className="break-words text-sm text-slate-700">{a.message}</p>
                      <p className="font-mono text-[11px] uppercase tracking-wide text-slate-400">{a.type.replaceAll("_", " ")}</p>
                    </div>
                  </div>
                  <span className="whitespace-nowrap text-xs text-slate-400">{timeAgo(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </ScrollReveal>
    </div>
  );
}
